import type { EventClock } from "../api";
import { label } from "./Money";

/**
 * How a clock reached the state it is in.
 *
 * The Clock model's own docstring says every field that drives a date is
 * stored "so that a clock's state can be re-derived and audited: we never
 * store only the answer" — and then every surface showed only the answer.
 * A contractual date somebody may act on should be checkable against the
 * contract without opening the database.
 *
 * Drata's control page decomposes readiness into what produced it. This is
 * the same, for a deadline: the date that started it, the period applied,
 * what that made due, what is recorded against it, and what happens next.
 *
 * The period is the load-bearing line. It is the difference between an
 * amended clause and the NEC4 default, and until now nothing showed which
 * one a date came from.
 */
export function ClockDerivation({ clock }: { clock: EventClock }) {
  const weeks = (days: number | null | undefined) =>
    days == null ? null : days % 7 === 0 ? `${days / 7} week${days === 7 ? "" : "s"}` : `${days} days`;

  const steps: Array<{ k: string; v: string; note?: string }> = [];

  steps.push({
    k: "Started",
    v: clock.trigger_date ?? "not recorded",
    note: "the date that set this clock running",
  });

  const reply = weeks(clock.reply_period_days);
  if (reply) {
    steps.push({
      k: "Period applied",
      v: reply,
      note: `from the confirmed ruleset for clause ${clock.clause}`,
    });
  }

  if (clock.reply_due_date) {
    steps.push({ k: "Reply due", v: clock.reply_due_date });
  }

  steps.push(
    clock.replied_date
      ? { k: "Replied", v: clock.replied_date }
      : { k: "Replied", v: "nothing recorded", note: "no reply is on the record" },
  );

  if (clock.reminder_notice_date) {
    steps.push({
      k: "Failure notified", v: clock.reminder_notice_date,
      note: "which started the further period",
    });
    const further = weeks(clock.further_period_days);
    if (further) {
      steps.push({ k: "Further period", v: further });
    }
  } else if (clock.further_period_days != null) {
    steps.push({
      k: "Failure notified", v: "not yet",
      note: "until it is, no automatic outcome is running",
    });
  }

  if (clock.deemed_date) {
    steps.push({
      k: "Outcome falls due", v: clock.deemed_date,
      note: "on this date, without anybody acting",
    });
  }

  return (
    <section className="deriv">
      <h3 className="deriv__title">
        How this reached “{label(clock.state)}”
      </h3>
      <ol className="deriv__steps">
        {steps.map((s, i) => (
          <li key={i} className="deriv__step">
            <span className="deriv__k">{s.k}</span>
            <span className="deriv__v mn">{s.v}</span>
            {s.note && <span className="deriv__note">{s.note}</span>}
          </li>
        ))}
      </ol>
      {clock.default_outcome && (
        <p className="deriv__outcome">
          <strong>If nobody acts:</strong> {clock.default_outcome}
        </p>
      )}
    </section>
  );
}
